import { useState } from "react";
import { redirectToLogin } from "../../utils/auth";

const emptyRole = { platform_admin: false, poc: false, customer_support: false, rcs_poc: false, rcs_csm: false, pmo: false };

// Edit a user (V2, PUT /users/:email). Email is immutable. RCS clients have no
// internal team/roles, so those are hidden for them.
export const EditUserModal = ({ setIsEditModalOpen, userToEdit, onSaved, onSuccess, teams, usertypes, roleColumns }) => {
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const isRcs = userToEdit.clientType === "RCS";
  const [form, setForm] = useState({
    name: userToEdit.name || "",
    phone: userToEdit.phone || "",
    team: userToEdit.team || "",
    usertype: userToEdit.usertype || "",
    altLoginEnabled: !!userToEdit.altLoginEnabled,
    role: { ...emptyRole, ...(userToEdit.role || {}) },
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const toggleRole = (key) => setForm((prev) => ({ ...prev, role: { ...prev.role, [key]: !prev.role[key] } }));

  const reonboard = async () => {
    if (
      !window.confirm(
        "Re-send onboarding? This clears the user's password and authenticator — they'll set both up again from the email link.",
      )
    )
      return;
    const authToken = localStorage.getItem("authToken");
    if (!authToken) { redirectToLogin(); return; }
    setError("");
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/API/V2/users/${encodeURIComponent(userToEdit.email)}/reonboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        onSaved && onSaved();
        setIsEditModalOpen(false);
        onSuccess && onSuccess(`Onboarding email re-sent to ${userToEdit.email}.`);
      } else {
        setError(data.error || "Failed to re-trigger onboarding.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setSaving(false);
  };

  const save = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required.");
    if (!isRcs && (!form.team || !form.usertype)) return setError("Internal staff need a team and user type.");

    // Send the full role object so nested flags aren't lost on update.
    const phone = form.phone.trim() || null;
    const body = isRcs
      ? { name: form.name.trim(), phone }
      : { name: form.name.trim(), phone, team: form.team, usertype: form.usertype, altLoginEnabled: form.altLoginEnabled, role: { ...emptyRole, ...form.role } };

    const authToken = localStorage.getItem("authToken");
    if (!authToken) { redirectToLogin(); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/API/V2/users/${encodeURIComponent(userToEdit.email)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        onSaved && onSaved();
        setIsEditModalOpen(false);
        onSuccess && onSuccess(`${form.name.trim()} has been updated successfully.`);
      } else {
        setError(data.error || "Failed to update user.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">Edit User</h2>
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 text-red-600 text-sm rounded border border-red-200">{error}</div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-4">
          <input
            type="email"
            value={userToEdit.email}
            className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-500"
            disabled
          />
          <input
            type="text"
            placeholder="Name"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            className="w-full border px-3 py-2 rounded"
            required
          />
          <input
            type="tel"
            placeholder="Phone (e.g. +9199...) — for priority-ticket call alerts"
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            className="w-full border px-3 py-2 rounded"
          />

          {!isRcs && (
            <>
              <select
                value={form.team}
                onChange={(e) => set({ team: e.target.value })}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">--Select Team--</option>
                {teams.map((t) => (
                  <option key={t.value} value={t.value}>{t.key}</option>
                ))}
              </select>
              <select
                value={form.usertype}
                onChange={(e) => set({ usertype: e.target.value })}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">--Select User Type--</option>
                {usertypes.map((u) => (
                  <option key={u.value} value={u.value}>{u.key}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {roleColumns.map((r) => (
                  <label key={r.key} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.role[r.key]}
                      onChange={() => toggleRole(r.key)}
                      className="mr-2"
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              <label className="flex items-center text-sm mt-1">
                <input
                  type="checkbox"
                  checked={form.altLoginEnabled}
                  onChange={(e) => set({ altLoginEnabled: e.target.checked })}
                  className="mr-2"
                />
                Alternate login (authenticator / email OTP)
              </label>
              {form.altLoginEnabled && !userToEdit.altLoginEnabled && (
                <p className="text-xs text-amber-600">
                  Enabling this resets the user's authenticator — they'll set it up again on next login.
                </p>
              )}
            </>
          )}
          {isRcs && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400">RCS clients have no internal team or roles.</p>
              <button
                type="button"
                onClick={reonboard}
                disabled={saving}
                className="w-full border border-jiojbmpurple text-jiojbmpurple hover:bg-jiojbm20 disabled:opacity-50 px-4 py-2 rounded text-sm font-semibold"
              >
                Re-trigger onboarding
              </button>
              <p className="text-xs text-amber-600">
                Resends the onboarding email and resets their password & authenticator.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-jiojbmpurple text-white px-4 py-2 rounded disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
